import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/** @typedef {import('./bundle-builder-store.js').BundleBuilderState} BundleBuilderState */
/** @typedef {import('./bundle-builder-tier-config.js').PurchaseType} PurchaseType */

const summaryOverlayMediaQuery = matchMedia('(max-width: 979px)');

export class BundleBuilderSummaryComponent extends BundleBuilderStoreMixinComponent {
  requiredRefs = [
    'summaryToggleButton',
    'summaryCloseButton',
    'summaryRegion',
  ];

  /** @type {AbortController | null} */
  #overlayController = null;

  /** @type {HTMLElement | null} */
  #previousActiveElement = null;

  /** @type {number} */
  #lockedScrollY = 0;

  /** @type {AbortController | null} */
  #wheelController = null;

  /** @type {boolean} */
  #previouslyMetMinimum = false;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.#syncExperimentClasses();
    this.#notifyAddToCartSummaryMode();
    this.#setupWheelScrollForwarding();
  }

  disconnectedCallback() {
    this.#wheelController?.abort();
    this.#wheelController = null;
    this.#closeSummary({ restoreFocus: false, animate: false });
    super.disconnectedCallback();
  }

  /**
   * @param {string} key
   * @param {unknown} value
   * @param {unknown} previousValue
   */
  onPropUpdate(key, value, previousValue) {
    if (key === 'experiments') {
      this.#syncExperimentClasses();
      this.#notifyAddToCartSummaryMode();
    }
  }

  onStateUpdate() {
    const nowMeetsMinimum = this.getters.meetsBundleMinimum();
    // activeSubscription hydrates async — re-sync so editor context drops v2 UI
    // (calendar toggle must stay available; ATC launch mode already bails in editor).
    const hadV2Class = this.classList.contains('has-experiment-summary-v2');
    this.#syncExperimentClasses();
    if (hadV2Class !== this.classList.contains('has-experiment-summary-v2')) {
      this.#notifyAddToCartSummaryMode();
    }

    if (this.#initialized && summaryOverlayMediaQuery.matches && !this.getters.isBundleEditorContext()) {
      if (nowMeetsMinimum && !this.#previouslyMetMinimum) {
        if (!this.#hasSummaryV2()) {
          this.#openSummary();
        }
      } else if (!nowMeetsMinimum && this.#previouslyMetMinimum) {
        this.#closeSummary({ restoreFocus: false });
      }
    }

    this.#previouslyMetMinimum = nowMeetsMinimum;
    this.#initialized = true;
  }

  /**
   * summary-v2 UI only for new-bundle mobile. Editor / cart-edit keep the calendar
   * toggle — ATC launch mode already skips editor, so hiding the toggle would leave
   * no way to open the drawer.
   * @returns {boolean}
   */
  #hasSummaryV2() {
    return this.experiments.includes('summary-v2') && !this.getters.isBundleEditorContext();
  }

  #syncExperimentClasses() {
    this.classList.toggle('has-experiment-summary-v2', this.#hasSummaryV2());
  }

  /**
   * ATC re-renders when summary open/closed changes (class-only, no store update).
   */
  #notifyAddToCartSummaryMode() {
    const atc = this.querySelector('bundle-builder-add-to-cart-component');
    if (atc && 'syncSummaryOpenMode' in atc && typeof atc.syncSummaryOpenMode === 'function') {
      atc.syncSummaryOpenMode();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFocusRestoreTarget() {
    if (this.#hasSummaryV2()) {
      const atcButton = this.querySelector('.bundle-builder__atc');
      return atcButton instanceof HTMLElement ? atcButton : null;
    }

    const toggle = this.refs.summaryToggleButton;
    return toggle instanceof HTMLElement ? toggle : null;
  }

  toggleSummary() {
    if (!summaryOverlayMediaQuery.matches) return;

    const region = /** @type {HTMLElement} */ (this.refs.summaryRegion);

    if (region.classList.contains('is-closing')) return;

    if (region.classList.contains('is-open')) {
      this.#closeSummary();
    } else {
      this.#openSummary();
    }
  }

  #openSummary() {
    if (this.#hasSummaryV2() && !this.getters.meetsBundleMinimum()) return;

    const region = /** @type {HTMLElement} */ (this.refs.summaryRegion);
    const toggle = /** @type {HTMLButtonElement} */ (this.refs.summaryToggleButton);

    // If a close animation is in progress, cancel it and keep the panel open.
    // Scroll is already locked — no need to re-lock.
    if (region.classList.contains('is-closing')) {
      region.classList.remove('is-closing');
      toggle.setAttribute('aria-expanded', 'true');
      this.#notifyAddToCartSummaryMode();
      requestAnimationFrame(() => trapFocus(region));
      this.#overlayController?.abort();
      this.#overlayController = new AbortController();
      this.addEventListener('keydown', this.#handleKeyDown, { signal: this.#overlayController.signal });
      return;
    }

    if (region.classList.contains('is-open')) return;

    this.#previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.#lockPageScroll();
    this.#toggleOpenSummaryClass(region, true);
    toggle.setAttribute('aria-expanded', 'true');
    this.#notifyAddToCartSummaryMode();

    requestAnimationFrame(() => {
      trapFocus(region);
    });

    this.#overlayController?.abort();
    this.#overlayController = new AbortController();
    this.addEventListener('keydown', this.#handleKeyDown, { signal: this.#overlayController.signal });
  }

  #closeSummary({ restoreFocus = true, animate = true } = {}) {
    const region = /** @type {HTMLElement} */ (this.refs.summaryRegion);
    const toggle = /** @type {HTMLButtonElement} */ (this.refs.summaryToggleButton);

    if (!region.classList.contains('is-open')) return;
    if (region.classList.contains('is-closing')) return;

    toggle.setAttribute('aria-expanded', 'false');
    removeTrapFocus();

    this.#overlayController?.abort();
    this.#overlayController = null;

    const focusTarget = restoreFocus ? this.#getFocusRestoreTarget() : null;

    this.#previousActiveElement = null;

    if (animate && summaryOverlayMediaQuery.matches) {
      region.classList.add('is-closing');

      region.addEventListener('animationend', () => {
        this.#toggleOpenSummaryClass(region, false);
        region.classList.remove('is-closing');
        this.#notifyAddToCartSummaryMode();
        this.#unlockPageScroll();
        focusTarget?.focus();
      }, { once: true });
    } else {
      this.#toggleOpenSummaryClass(region, false);
      this.#notifyAddToCartSummaryMode();
      requestAnimationFrame(() => {
        this.#unlockPageScroll();
        focusTarget?.focus();
      });
    }
  }

  /**
   * @param {HTMLElement} region
   * @param {boolean} open
   */
  #toggleOpenSummaryClass(region, open = true) {
    if (open) {
      region.classList.add('is-open');
      this.classList.add('is-open');
    } else {
      region.classList.remove('is-open');
      this.classList.remove('is-open');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#closeSummary();
  };

  #setupWheelScrollForwarding() {
    const region = /** @type {HTMLElement} */ (this.refs.summaryRegion);

    this.#wheelController?.abort();
    this.#wheelController = new AbortController();

    region.addEventListener('wheel', this.#handleSummaryWheel, {
      passive: false,
      signal: this.#wheelController.signal,
    });
  }

  /**
   * Forwards wheel input from the summary header/footer to the scrollable list.
   *
   * @param {WheelEvent} event
   */
  #handleSummaryWheel = (event) => {
    const region = event.currentTarget;
    if (!(region instanceof HTMLElement)) return;

    const scrollable = region.querySelector('.bundle-builder__summary-body__scrollable');
    if (!(scrollable instanceof HTMLElement)) return;
    if (scrollable.scrollHeight <= scrollable.clientHeight) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollable;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
    const scrollingUp = event.deltaY < 0;
    const scrollingDown = event.deltaY > 0;
    const canScroll = (scrollingUp && !atTop) || (scrollingDown && !atBottom);

    const targetInScrollable =
      event.target instanceof Element &&
      event.target.closest('.bundle-builder__summary-body__scrollable');

    if (targetInScrollable && canScroll) return;

    if (!canScroll) return;

    event.preventDefault();
    scrollable.scrollTop += event.deltaY;
  };

  #lockPageScroll() {
    if (!summaryOverlayMediaQuery.matches) return;

    this.#lockedScrollY = window.scrollY;
    document.documentElement.setAttribute('scroll-lock', '');

    if (this.#lockedScrollY > 0) {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#lockedScrollY}px`;
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  #unlockPageScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.overflow = '';
    window.scrollTo({ top: this.#lockedScrollY, behavior: 'instant' });
  }
}

if (!customElements.get('bundle-builder-summary-component')) {
  customElements.define('bundle-builder-summary-component', BundleBuilderSummaryComponent);
}
